<script setup lang="ts">
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import '@/assets/css/swiper.css'
  import type { TestimonialSliderStoryblok, TestimonialSlideStoryblok } from '@/storyblok/component-types-sb'
  import { SCREEN_MD } from '@/tailwind.config'
  import useCLPDataLayer from '@/composables/data-layers/useCLPDataLayer'

  const { isDesktop } = useBTBreakpoints()

  const props = defineProps<{
    blok: TestimonialSliderStoryblok
  }>()

  const slides = ref<TestimonialSlideStoryblok[]>([])

  const shuffledSlides = () => {
    const propSlides = [...props.blok.slides]
    let i = propSlides.length,
      j,
      temp

    while (--i > 0) {
      j = Math.floor(Math.random() * (i + 1))
      temp = propSlides[j]
      propSlides[j] = propSlides[i]
      propSlides[i] = temp
    }

    slides.value = propSlides
  }

  shuffledSlides()

  const sliderSettings: SwiperSettings = {
    speed: 500,
    keyboard: true,
    slidesPerView: 'auto',
    initialSlide: 1,
    spaceBetween: '16px',
    loop: true,
    breakpoints: {
      [SCREEN_MD]: {
        keyboard: false,
        slidesPerView: 2,
        slidesPerGroup: 2,
        navigation: {
          nextEl: '#next-btn-testimonial',
          prevEl: '#prev-btn-testimonial'
        },
        loop: false
      }
    },
    watchSlidesProgress: true
  }

  const testimonialSlider = ref(null)
  const targetIsVisible = useElementVisibility(testimonialSlider)
  const showElement = ref(false)
  watchOnce(targetIsVisible, (val) => {
    if (val) {
      showElement.value = true
      onTestimonialSliderSeen()
    }
  })

  const { pushCLPDataLayer } = useCLPDataLayer()
  function onTestimonialSliderSeen() {
    pushCLPDataLayer({
      generic_event_name: 'reviews_section',
      event_category: 'seen'
    })
  }

  function onTestimonialSliderSlide() {
    pushCLPDataLayer({
      generic_event_name: 'reviews_section',
      event_category: 'arrow_click'
    })
  }
</script>
<template>
  <div
    ref="testimonialSlider"
    class="mx-auto pb-4 pt-10 sm:pb-6 md:py-20"
  >
    <template v-if="showElement">
      <div class="flex flex-col px-5 md:items-center md:px-0">
        <label
          v-if="blok.title"
          class="mb-2 text-secondary text-h5"
          aria-labelledby="testimonial-subtitle"
        >
          {{ blok.title }}
        </label>
        <h2
          v-if="blok.subTitle"
          id="testimonial-subtitle"
        >
          {{ blok.subTitle }}
        </h2>
      </div>
      <div class="relative mb-8 pl-5 md:container lg:mb-14 lg:pl-0">
        <UiSliderComponent
          :slider-settings="sliderSettings"
          :slides="slides"
          :partial-slide="!isDesktop"
          :tabindex="isDesktop ? -1 : 0"
          slide-class="max-w-[80%] sm:max-w-[40%] lg:max-w-full"
          @swipernavigationprev="onTestimonialSliderSlide"
          @swipernavigationnext="onTestimonialSliderSlide"
        >
          <template #slide="{ slide }">
            <div class="pb-10 pt-6 md:mr-0 md:px-3 md:pb-12 md:pt-5">
              <UiSliderTestimonialCard
                tabindex="0"
                :text="slide.text"
                :sub-text="slide.subText"
              />
            </div>
          </template>

          <template #slider-outside>
            <UiSliderNavigationRoundArrows
              class="z-[1] hidden h-10 justify-center gap-4 md:flex"
              prev-btn-id="prev-btn-testimonial"
              next-btn-id="next-btn-testimonial"
            >
            </UiSliderNavigationRoundArrows>
          </template>
        </UiSliderComponent>
      </div>
    </template>
  </div>
</template>
